import React from "react";
import { useTranslation } from "../../../localization/useTranslation";
import { useSystemDocuments } from "./SystemDocuments.hooks";
import DotLegalPaper from "../../../common/components/dotLegalPaper/DotLegalPaper";
import { DotLegalButton, DotLegalEmptyState, DotLegalOverflowMenu, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { Box, TableCell, TableRow } from "@mui/material";
import NoSharings from "../../../processingActivity/processingActivityOverview/processingActivitySharingsTab/noSharings.svg?react";
import { SystemDocumentRowViewModel } from "./SystemDocuments.types";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import SystemDocumentDialog from "./systemDocumentDialog/SystemDocumentDialog";
import SystemDocumentDeleteDialog from "./systemDocumentDeleteDialog/SystemDocumentDeleteDialog";
import DocumentPreviewLink from "../../../common/components/documentPreview/DocumentPreviewLink";
import { useDocumentPreviewManager } from "../../../common/components/documentPreview/useDocumentPreviewManager";
import { EntityType } from "../../../common/Common.types";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import TableHeaderLock from "../../../common/components/TableHeaderLock";
import { isNullOrWhitespace } from "../../../common/stringOperations.ts";
import AuditLogDialog from "../../../auditlogDialog/AuditLogDialog.tsx";
import { useAiFeatureAccess } from "../../../plan/aiFeatureGate/useAiFeatureAccess";
import DocumentAccessCell from "../../../common/components/documentAccessCell/DocumentAccessCell";
import BestPracticeCoverageResult from "../../../common/components/bestPracticeCheckResultDialog/BestPracticeCoverageResult.tsx";
import BestPracticeCheckDialog from "../../../common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.tsx";
import BestPracticeCheckResultDialog from "../../../common/components/bestPracticeCheckResultDialog/BestPracticeCheckResultDialog.tsx";
import { DownloadAllDocumentsButton } from "@common/components/downloadAllDocuments/DownloadAllDocumentsButton";

export interface SystemDocumentTabProps {
    systemId?: string;
    systemName?: string;
    refetch: () => void;
}

function SystemDocumentTab(props: SystemDocumentTabProps) {
    const { translateString, translateDate } = useTranslation();
    const { permissions } = useUserContext();
    const { showAiQualityAssurance, planActivated } = useAiFeatureAccess();
    const {
        isLoading,
        data,
        hasDocuments,
        refetchDocuments,
        getMenuItems,
        selectedDocument,
        setSelectedDocument,
        showDocumentDialog,
        setShowDocumentDialog,
        showDeleteDialog,
        setShowDeleteDialog,
        showAuditLogDialog,
        setShowAuditLogDialog,
        bestPracticeCheckDocument,
        setBestPracticeCheckDocument,
        bestPracticeCheckDocumentToQualityAssurance,
        setBestPracticeCheckDocumentToQualityAssurance,
        bestPracticeCheckInitialResult,
        setBestPracticeCheckInitialResult,
        onCheckComplete,
        downloadingDocument,
        onDownloadClick,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useSystemDocuments(props);

    const { previewableDocuments, handleDocumentClick, getPreviewHref } = useDocumentPreviewManager({
        documents: data ?? [],
        entityType: EntityType.System,
        entityId: props.systemId!,
        entityTitle: props.systemName,
    });

    const isDocumentPreviewable = (documentId: string) => previewableDocuments.some((d) => d.documentId === documentId);

    return (
        <DotLegalPaper>
            {hasDocuments ? (
                <DotLegalTableWithFilterBar
                    hasOverflowMenu
                    filterBar={{
                        isLoading: isLoading,
                        filterData: getFilterOptions(),
                        fetchFilterData: true,
                        onMenuClose: onFilterChange,
                        onRemove: onFilterChange,
                        pinnedFilterIds: pinnedFilterIds,
                        onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                    }}
                    headers={GetDocumentHeader(translateString, showAiQualityAssurance, planActivated)}
                    rowsPerPage={30}
                    defaultOrderBy={"created"}
                    defaultOrder={"desc"}
                    isLoading={isLoading}
                    data={data}
                    clickableRows
                    emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noSharingsMatchesQuery")}
                    renderRow={(row, i) => (
                        <TableRow key={i} onClick={() => handleDocumentClick(row)}>
                            <TableCell>
                                <Box sx={(theme) => ({ color: theme.palette.primary.main, fontWeight: theme.typography.fontWeightBold })}>
                                    <DocumentPreviewLink previewHref={getPreviewHref(row)} onPreviewClick={() => handleDocumentClick(row)}>
                                        {row.name}
                                    </DocumentPreviewLink>
                                </Box>
                            </TableCell>
                            <TableCell>{row.fileName ?? row.link}</TableCell>
                            <TableCell>{translateDate(row.created)}</TableCell>
                            <DocumentAccessCell
                                documentId={row.documentId}
                                link={row.link}
                                downloadingDocument={downloadingDocument}
                                isPreviewable={isDocumentPreviewable(row.documentId)}
                                previewHref={getPreviewHref(row)}
                                onDownloadClick={() => onDownloadClick(row.documentId, row.dataId!, row.fileName!, row.link)}
                                onPreviewClick={() => handleDocumentClick(row)}
                            />
                            {showAiQualityAssurance && (
                                <TableCell>
                                    {!isNullOrWhitespace(row.fileName) && (
                                        <BestPracticeCoverageResult
                                            result={row.aiQualityAssuranceResult}
                                            isTooLargeForAi={row.isTooLargeForAi}
                                            onClick={() => {
                                                if (row.aiQualityAssuranceResult) {
                                                    setBestPracticeCheckDocument({
                                                        documentId: row.documentId,
                                                    });
                                                } else {
                                                    setBestPracticeCheckDocumentToQualityAssurance({
                                                        documentId: row.documentId,
                                                    });
                                                }
                                            }}
                                        />
                                    )}
                                </TableCell>
                            )}
                            <TableCell align={"right"}>
                                <DotLegalOverflowMenu items={getMenuItems(row)} />
                            </TableCell>
                        </TableRow>
                    )}
                />
            ) : (
                <DotLegalEmptyState icon={<NoSharings />} text={translateString("noDocumentsAttachedToLegalEntity")} />
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2, gap: 2 }}>
                <DownloadAllDocumentsButton entityType={EntityType.System} entityId={props.systemId!} show={(data?.length ?? 0) > 1} />
                {permissions.systemPermissions.edit && (
                    <DotLegalButton
                        buttonType={"primary"}
                        onClick={() => {
                            setSelectedDocument(undefined);
                            setShowDocumentDialog(true);
                        }}
                    >
                        {translateString("addDocument")}
                    </DotLegalButton>
                )}
            </Box>

            {showDocumentDialog && (
                <SystemDocumentDialog
                    onDialogClose={() => setShowDocumentDialog(false)}
                    selectedDocument={selectedDocument}
                    onSave={() => refetchDocuments()}
                />
            )}

            {showDeleteDialog && (
                <SystemDocumentDeleteDialog
                    onCloseDialog={() => setShowDeleteDialog(false)}
                    onDeletedDocument={() => refetchDocuments()}
                    systemId={props.systemId}
                    selectedDocument={selectedDocument!}
                />
            )}

            {!isNullOrWhitespace(showAuditLogDialog) && (
                <AuditLogDialog
                    entityType={EntityType.SystemDocument}
                    entityId={showAuditLogDialog!}
                    entityName={(data ?? []).find((x) => x.systemDocumentId === showAuditLogDialog)?.name ?? ""}
                    onDialogClose={() => setShowAuditLogDialog(null)}
                />
            )}

            {bestPracticeCheckDocumentToQualityAssurance && (
                <BestPracticeCheckDialog
                    documentId={bestPracticeCheckDocumentToQualityAssurance.documentId}
                    onDialogClose={() => {
                        setBestPracticeCheckDocumentToQualityAssurance(undefined);
                        refetchDocuments();
                    }}
                    onCheckComplete={onCheckComplete}
                />
            )}

            {bestPracticeCheckDocument && (
                <BestPracticeCheckResultDialog
                    documentId={bestPracticeCheckDocument.documentId}
                    initialResult={bestPracticeCheckInitialResult}
                    onDialogClose={() => {
                        setBestPracticeCheckDocument(undefined);
                        setBestPracticeCheckInitialResult(undefined);
                        refetchDocuments();
                    }}
                />
            )}
        </DotLegalPaper>
    );
}

export default SystemDocumentTab;

function GetDocumentHeader(translateString: any, showAiQualityAssurance: boolean, planActivated: boolean) {
    const headers = Array<ITableHeader<SystemDocumentRowViewModel>>();
    headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
    headers.push({ property: "fileName", text: translateString("document"), align: "left", showOnMobile: true });
    headers.push({ property: "created", text: translateString("created"), align: "left", showOnMobile: true });
    headers.push({ property: "dataId", text: translateString("accessDocument") });
    if (showAiQualityAssurance) {
        headers.push({
            property: "aiQualityAssuranceResult",
            text: translateString("aiQualityScan"),
            extraHeaderContent: !planActivated ? <TableHeaderLock tooltip={translateString("aiAddOnPurchaseTooltip")} /> : undefined,
        });
    }
    return headers;
}
